import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const [companyInfo, setCompanyInfo] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminLogin, setAdminLogin] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const { login, lookupCompany, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate(user.role === 'super_admin' ? '/admin-secret' : '/dashboard');
    }
  }, [user, navigate]);

  // Empresa fixa: entra direto na página de login da empresa
  useEffect(() => {
    lookupCompany('tomaselli')
      .then(setCompanyInfo)
      .catch(() => setCompanyInfo({ slug: 'tomaselli', name: 'Tomaselli' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Acesso via query param ?admin=1
  useEffect(() => {
    if (searchParams.get('admin') === '1') {
      setAdminLogin(true);
    }
  }, [searchParams]);

  // Acesso oculto: 5 cliques no logo
  const handleLogoClick = () => {
    const newCount = logoClicks + 1;
    setLogoClicks(newCount);
    if (newCount >= 5) {
      setAdminLogin(true);
      setLogoClicks(0);
    }
    setTimeout(() => setLogoClicks(0), 2000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preencha todos os campos');
      return;
    }
    setLoading(true);
    try {
      const slug = adminLogin ? null : (companyInfo?.slug || 'tomaselli');
      const userData = await login(email, password, slug);
      toast.success(`Bem-vindo, ${userData.name}!`);
      navigate(userData.role === 'super_admin' ? '/admin-secret' : '/dashboard');
    } catch (error) {
      toast.error(error.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setEmail('');
    setPassword('');
    setAdminLogin(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-stone-950 via-stone-900 to-black relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gold gradient orbs */}
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-gradient-to-br from-amber-500/10 to-yellow-600/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-gradient-to-br from-amber-400/10 to-amber-600/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-amber-500/5 to-transparent rounded-full blur-3xl" />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(251,191,36,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(251,191,36,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />
        
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-amber-500/20" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-amber-500/20" />
      </div>

      {/* Main Container */}
      <div className="w-full max-w-md px-6 relative z-10">
        {/* Logo/Header */}
        <header className="pb-10 text-center">
          <div 
            onClick={handleLogoClick}
            className="inline-block cursor-pointer select-none"
          >
            <p className="text-sm sm:text-base text-stone-500 tracking-[0.2em] font-light">
              acesso restrito
            </p>
          </div>
        </header>

        {/* Aviso de descontinuação */}
        <div data-testid="aviso-descontinuacao-login" className="mb-6 rounded-xl border border-red-500/40 bg-red-950/40 px-4 py-3">
          <p className="text-xs leading-relaxed text-red-400 text-center">
            <span className="font-bold uppercase">Aviso:</span> este sistema será descontinuado.
            Use o <span className="font-semibold">ponto da Flash</span> e as soluções integradas de RH da Flash.
            A gestão operacional passa a ser no <span className="font-semibold">SlapFestas</span>, sistema escolhido pelo Tomaselli.
          </p>
        </div>

        {/* Form Container */}
        <main className="relative">
          {/* Card background */}
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/80 to-stone-950/90 rounded-2xl backdrop-blur-xl border border-amber-500/10 shadow-2xl shadow-black/50" />
          
          <div className="relative p-8">
            <form onSubmit={handleLogin} className="space-y-5">
                {/* Badge do modo Super Admin */}
                {adminLogin && (
                <div className="flex items-center gap-3 mb-6">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-800/50 text-stone-400 hover:text-amber-400 hover:bg-stone-800 border border-stone-700/50 transition-all duration-300"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/20">
                    <p className="text-[10px] text-amber-500/60 uppercase tracking-widest">
                      Modo
                    </p>
                    <p className="text-sm font-medium text-stone-200 truncate">
                      Super Admin
                    </p>
                  </div>
                </div>
                )}

                <div className="space-y-2">
                  <Label className="text-sm text-amber-500/80 font-medium tracking-wide">
                    Email
                  </Label>
                  <Input
                    type="email"
                    data-testid="login-email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-14 text-base bg-stone-950/50 border-stone-700/50 text-stone-100 placeholder:text-stone-600 rounded-xl focus:border-amber-500/50 focus:ring-amber-500/20 transition-all duration-300"
                    disabled={loading}
                    autoFocus
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-amber-500/80 font-medium tracking-wide">
                    Senha
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      data-testid="login-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-14 text-base bg-stone-950/50 border-stone-700/50 text-stone-100 placeholder:text-stone-600 rounded-xl pr-14 focus:border-amber-500/50 focus:ring-amber-500/20 transition-all duration-300"
                      disabled={loading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-500 hover:text-amber-400 transition-colors p-1"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="login-submit"
                  className="w-full h-14 text-base font-semibold bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:via-amber-400 hover:to-amber-500 text-stone-950 rounded-xl transition-all duration-300 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/30 tracking-wide mt-2"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </form>
          </div>
        </main>

        {/* Footer */}
        <footer className="pt-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500/30" />
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500/40" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500/30" />
          </div>
          <p className="text-xs text-stone-600 tracking-wider">
            © {new Date().getFullYear()}
          </p>
        </footer>
      </div>
    </div>
  );
};

export default LoginPage;
